import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { flushOfflineQueue, subscribeOfflineQueue, pendingOfflineCount } from '../services/api';

/**
 * Connectivity strip: tells the expert whether they are offline and how many
 * changes are still waiting to reach the server. Silent when everything is
 * online and in sync.
 */
export default function OfflineBanner() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    const unsubscribe = subscribeOfflineQueue(setPending);
    pendingOfflineCount().then(setPending).catch(() => {});
    // Replay anything left over from a previous session.
    if (navigator.onLine) flushOfflineQueue().catch(() => {});

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      unsubscribe();
    };
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await flushOfflineQueue();
    } finally {
      setSyncing(false);
    }
  };

  if (online && pending === 0) return null;

  return (
    <div className={`offline-banner ${online ? 'is-pending' : 'is-offline'}`} role="status">
      <span className="offline-dot" />
      <span>
        {online ? t('offline.pending', { count: pending }) : t('offline.offline')}
        {!online && pending > 0 && ` — ${t('offline.pending', { count: pending })}`}
      </span>
      {online && pending > 0 && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleSync} disabled={syncing}>
          {syncing ? t('offline.syncing') : t('offline.syncNow')}
        </button>
      )}
    </div>
  );
}
